import { FunctionComponent, MouseEventHandler, useMemo } from 'react'
import styled, { css } from 'styled-components'
import IconLock from '../../../../shared/assets/icons/icon-lock.svg'
import IconUnlock from '../../../../shared/assets/icons/icon-unlock.svg'
import useWindowWidth from '../../../../shared/hooks/useWindowWidth'
import { Breakpoint } from '../../../../shared/hooks/useBreakpoint'
import Icon from '../../../../shared/components/Icon/Icon'
import Label, { Position } from '../../../../shared/components/Label/Label'
import ChevronDown from '../../../../shared/assets/icons/chevronDown.svg'

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`

interface LayerButtonProps {
  isOpen?: boolean
  hasActiveLayer?: boolean
}

const LayerButton = styled.button<LayerButtonProps>`
  display: flex;
  cursor: pointer;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 1rem;
  padding: 0 0.75rem;
  background-color: var(--greyUltraLight);
  margin-bottom: 0.25rem;
  ${({ hasActiveLayer }) =>
    hasActiveLayer &&
    css`
      background-color: var(--yellow);
    `}
`

const MobileLayerButton = styled.button<LayerButtonProps>`
  display: flex;
  cursor: pointer;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: var(--light);
  padding: 0;

  label {
    font-weight: 800;
    font-size: 1.125rem;
    line-height: 1.4;
    color: var(--blue);
  }

  ${({ isOpen }) =>
    !isOpen &&
    css`
      margin-bottom: 0.5rem;
    `}
`

const IconLockWrapper = styled(Icon)`
  margin-right: 0.9375rem;
  @media screen and (max-width: 48rem) {
    margin-right: 0.5rem;
  }
`

interface LayerCollectionButtonProps {
  title: string
  allVisible: boolean
  collectionIndeterminate: boolean
  isOpen: boolean
  isLayerRestricted?: boolean
  isUserAuthorised?: boolean
  onClick: MouseEventHandler<HTMLButtonElement>
}

const LayerCollectionButton: FunctionComponent<
  LayerCollectionButtonProps & Partial<HTMLButtonElement>
> = ({
  title,
  allVisible,
  collectionIndeterminate,
  className,
  isOpen,
  isLayerRestricted = false,
  isUserAuthorised = true,
  onClick,
}) => {
  const width = useWindowWidth()
  const isDesktop = width > Breakpoint.Md.valueOf()
  const testId: string = useMemo(
    () =>
      title
        .split(' ')
        .map((word: string) => `${word.charAt(0).toUpperCase()}${word.substring(1)}`)
        .join(''),
    [title],
  )

  if (isDesktop) {
    return (
      <LayerButton
        data-testid={`mapLegendLayerButton${testId}`}
        onClick={onClick}
        isOpen={isOpen}
        hasActiveLayer={collectionIndeterminate || allVisible}
        className={className}
      >
        <Wrapper>
          <Label key={title} label={title} position={Position.Left} />
        </Wrapper>
        <Wrapper>
          {isLayerRestricted &&
            (!isUserAuthorised ? (
              <IconLockWrapper size={15}>
                <IconLock />
              </IconLockWrapper>
            ) : (
              <IconLockWrapper size={15}>
                <IconUnlock />
              </IconLockWrapper>
            ))}
          <Icon size={15} rotate={isOpen ? 180 : 0}>
            <ChevronDown />
          </Icon>
        </Wrapper>
      </LayerButton>
    )
  }

  return (
    <MobileLayerButton
      data-testid={`mapLegendLayerButton${testId}`}
      onClick={onClick}
      isOpen={isOpen}
      hasActiveLayer={collectionIndeterminate || allVisible}
      className={className}
    >
      <Wrapper>
        {isLayerRestricted &&
          (!isUserAuthorised ? (
            <IconLockWrapper size={16}>
              <IconLock />
            </IconLockWrapper>
          ) : (
            <IconLockWrapper size={16}>
              <IconUnlock />
            </IconLockWrapper>
          ))}
        <Label key={title} label={title} position={Position.Left} />
      </Wrapper>
      <Wrapper>
        <Icon size={24} color="var(--blue)" rotate={isOpen ? 180 : 0}>
          <ChevronDown />
        </Icon>
      </Wrapper>
    </MobileLayerButton>
  )
}

export default LayerCollectionButton
